/**
 * Publish-failure truth, at the grain the failure actually happened on.
 *
 * 🔴 Read this before changing any number this module produces.
 *
 * Prod on 2026-08-08 holds 148 `social_posts` rows with `status='failed'`. That
 * number is NOT the number of failures, and shipping it would be the product
 * lying loudly rather than lying by omission:
 *
 *   - 136 of the 148 carry the literal error string
 *     `Processing timeout: stuck in publishing (F-123 historical backlog cleanup 2026-06-14)`.
 *     They were bulk-stamped by a maintenance script, not by a publish attempt.
 *   - 12 are real, and only 2 of the 148 have any `social_post_targets` row at all.
 *
 * `useNeedsAttention` counts at post grain and therefore currently reports 169
 * ("needs attention") on prod, of which 136 are that backfill marker — an 11x
 * inflation of a number the user is asked to act on.
 *
 * This module works at TARGET grain (`social_post_targets`), which is both the
 * correct per-connection grain and — because the backfill never fanned out —
 * naturally free of that noise. There is deliberately NO string-matching against
 * the F-123 marker anywhere in this file: excluding noise by parsing an error
 * message would be a hack that silently breaks the day the message changes. The
 * exclusion is structural, and the posts that never fanned out are reported
 * separately (see `OrphanedFailureSummary`) rather than dropped.
 *
 * 🔴 Identity: `social_post_targets` is account-scoped and its RLS policy
 * resolves `current_account_owner_id()` server-side through the parent post
 * (verified against live `pg_policies`, 2026-08-08; the checked-in registry
 * agrees at `table-scope.generated.ts:89`). Callers must NOT add `.eq('user_id',
 * …)` — the table has no such column, and resolving an id by hand here would
 * reintroduce the member-vs-owner bug rather than prevent it.
 */

import { platformOutcome } from '../../../shared/platform-outcome';
import { isValidPlatformPostId } from '../../../shared/platform-post-id';
import { CHECK_PLATFORM_CODE } from '../../../shared/provider-outcome';
import { publishFailureMessage } from '../../../shared/publish-failure-message';
import { getPlatformLabel } from '../platforms/identity';
import { isUnsupportedPublishPlatform } from '../posts/unsupported-publish';

export type DeliveryStatus =
  | 'unscheduled'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'published_with_warnings'
  | 'manual_action_required'
  | 'failed'
  | 'native_status_unknown';

/** Delivery states that are still moving. The ledger watches these. */
export const IN_FLIGHT_DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'scheduled',
  'publishing',
] as const;

/** Delivery states that need a human. Wider than `failed` on purpose. */
export const ATTENTION_DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'failed',
  'manual_action_required',
  'native_status_unknown',
] as const;

export interface PublishTargetRow {
  id: string;
  postId: string;
  platform: string;
  connectionId: string;
  connectionName: string | null;
  deliveryStatus: DeliveryStatus;
  errorCode: string | null;
  errorMessage: string | null;
  attemptCount: number;
  nativePostId: string | null;
  permalink: string | null;
  scheduledAt: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

export type DeliveryTargetAction =
  | { kind: 'reconnect'; label: string; href: '/settings?connect=1' }
  | {
      kind: 'open_post';
      label:
        | 'Open post'
        | 'Review post'
        | 'Review warning'
        | 'Review required step'
        | `Check ${string} status`;
    }
  | { kind: 'external'; label: string; href: string }
  | null;

export function needsDeliveryAttention(status: DeliveryStatus): boolean {
  return ATTENTION_DELIVERY_STATUSES.includes(status) || status === 'published_with_warnings';
}

export function resolveDeliveryTargetAction(target: PublishTargetRow): DeliveryTargetAction {
  if (target.deliveryStatus === 'published' && target.permalink) {
    return {
      kind: 'external',
      label: `View on ${platformLabel(target.platform)}`,
      href: target.permalink,
    };
  }
  if (target.deliveryStatus === 'published_with_warnings') {
    return { kind: 'open_post', label: 'Review warning' };
  }
  if (target.deliveryStatus === 'native_status_unknown') {
    return { kind: 'open_post', label: `Check ${platformLabel(target.platform)} status` };
  }
  if (target.deliveryStatus === 'scheduled') {
    return { kind: 'open_post', label: 'Open post' };
  }
  if (target.deliveryStatus === 'manual_action_required') {
    if (target.errorCode === CHECK_PLATFORM_CODE) {
      return { kind: 'open_post', label: `Check ${platformLabel(target.platform)} status` };
    }
    return { kind: 'open_post', label: 'Review required step' };
  }
  if (!needsDeliveryAttention(target.deliveryStatus)) return null;
  if (classifyFailure(target.errorMessage) === 'credential') {
    return {
      kind: 'reconnect',
      label: `Reconnect ${platformLabel(target.platform)}`,
      href: '/settings?connect=1',
    };
  }
  return { kind: 'open_post', label: 'Review post' };
}

/**
 * The platform's own words, dug out of the envelope Mora wraps them in.
 *
 * Stored messages look like:
 *   `Platform error: Request failed with status 403: {"error":{"message":"(#200) …
 *
 * Showing that raw is unreadable; paraphrasing it would violate the rule that a
 * platform error is quoted, never reworded. So this extracts the provider's
 * `message` verbatim when one is embedded, and otherwise returns the string
 * unchanged. It never invents, shortens to a summary, or maps to friendlier
 * copy — an unrecognised shape falls through as-is.
 */
export function extractProviderMessage(raw: string | null): string | null {
  const extracted = extractRawProviderMessage(raw);
  return extracted ? tidyProviderText(extracted) : extracted;
}

function tidyProviderText(text: string): string {
  return text
    .replace(/\\+(?=\s)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractRawProviderMessage(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const braceAt = trimmed.indexOf('{');
  if (braceAt === -1) return trimmed;

  const candidate = trimmed.slice(braceAt);
  try {
    const parsed: unknown = JSON.parse(candidate);
    const message = readNestedMessage(parsed);
    if (message) return message;
  } catch {
    // Not JSON, or truncated mid-object. Fall through to the raw string —
    // an unparseable payload is still the truth, just an ugly one.
  }
  return trimmed;
}

function readNestedMessage(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }
  for (const key of ['error', 'data', 'error_description']) {
    const nested = record[key];
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
    const deeper = readNestedMessage(nested);
    if (deeper) return deeper;
  }
  return null;
}

/**
 * Collapse the volatile parts of a provider message so two attempts that failed
 * the same way group together. Meta stamps a unique `fbtrace_id` on every 403,
 * so without this every Facebook rejection would look like its own distinct
 * problem and the strip would read "5 issues" for one broken connection.
 */
export function normalizeFailureReason(raw: string | null): string {
  const message = extractProviderMessage(raw);
  if (!message) return 'unknown';
  return message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
    .replace(/\bA[A-Za-z0-9_-]{15,}\b/g, '')
    .replace(/\b\d{6,}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * What kind of thing went wrong — decided from the provider's message, because
 * `error_code` is `provider_rejected` on every single failed row in prod and so
 * carries no signal at all.
 *
 * This drives which remedy is offered, so each arm must be defensible:
 *
 *  - `credential`  the stored token cannot do the thing being asked. Retrying
 *                  replays the same rejection; only reconnecting can help.
 *  - `media`       an asset could not be prepared for this platform. The post
 *                  itself is the place to fix it.
 *  - `destination` the post never resolved a real account to send to.
 *  - `unknown`     say so. Never guess a remedy for a message we don't know.
 */
export type FailureKind = 'credential' | 'media' | 'destination' | 'timeout' | 'unknown';

export function classifyFailure(raw: string | null): FailureKind {
  // Matched against the RAW envelope, not just the extracted message. TikTok
  // puts its real signal in `error.code`
  // (`unaudited_client_can_only_post_to_private_accounts`) while `error.message`
  // is the useless "Please review our integration guidelines" — classifying off
  // the extracted message alone read that as `unknown` and offered no remedy for
  // a plainly credential-shaped problem. Caught by a test built from the real
  // prod string, which is why the tests use verbatim payloads.
  const message = `${extractProviderMessage(raw) ?? ''} ${raw ?? ''}`.toLowerCase();
  if (!message.trim()) return 'unknown';

  if (
    message.includes('must be posted to a page as the page itself') ||
    message.includes('access token') ||
    message.includes('oauthexception') ||
    message.includes('permission') ||
    message.includes('unaudited_client') ||
    message.includes('expired') ||
    message.includes('revoked')
  ) {
    return 'credential';
  }
  if (message.includes('could not be prepared') || message.includes('media')) {
    return 'media';
  }
  if (message.includes('no active') || message.includes('destinations are incomplete')) {
    return 'destination';
  }
  // Mora stopped waiting; nothing says the platform refused the post (founder, 3 Nov 2026: "did not accept this
  // post" was shown for posts that only timed out).
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('stuck in publishing')
  ) {
    return 'timeout';
  }
  return 'unknown';
}

export interface PublishFailureGroup {
  /** Stable identity for this family of failures. */
  key: string;
  platform: string;
  connectionId: string;
  connectionName: string | null;
  kind: FailureKind;
  /** The provider's own message, verbatim. */
  reason: string | null;
  targetIds: string[];
  postIds: string[];
  count: number;
  lastFailedAt: string;
  /**
   * True when this connection has never once published successfully. The most
   * damning honest fact available and the reason the group sorts first.
   */
  neverSucceeded: boolean;
  /**
   * Retry is offered only when replaying the send cannot double-post and could
   * plausibly succeed. See `canRetry` for the two conditions.
   */
  retryable: boolean;
}

/**
 * Retry is safe AND worth offering only when both hold:
 *
 *  1. No `native_post_id` on any target in the group. A native id means the
 *     platform accepted something; replaying could publish twice. Mora has no
 *     way to un-publish, so this is a hard gate, not a heuristic.
 *  2. The failure is not a credential problem. A page-token rejection retried
 *     with the same page token fails identically — offering "Retry" there is a
 *     button that promises a fix it cannot deliver.
 */
function canRetry(rows: PublishTargetRow[], kind: FailureKind): boolean {
  if (kind === 'credential' || kind === 'destination') return false;
  return rows.every((row) => !row.nativePostId);
}

export function groupPublishFailures(
  rows: PublishTargetRow[],
  publishedByPlatform: Record<string, number> = {},
): PublishFailureGroup[] {
  const buckets = new Map<string, PublishTargetRow[]>();

  for (const row of rows) {
    const key = `${row.platform}|${row.connectionId}|${normalizeFailureReason(row.errorMessage)}`;
    const existing = buckets.get(key);
    if (existing) existing.push(row);
    else buckets.set(key, [row]);
  }

  const groups: PublishFailureGroup[] = [];
  for (const [key, bucket] of buckets) {
    const newest = bucket.reduce((a, b) => (a.updatedAt >= b.updatedAt ? a : b));
    const kind = classifyFailure(newest.errorMessage);
    groups.push({
      key,
      platform: newest.platform,
      connectionId: newest.connectionId,
      connectionName: newest.connectionName,
      kind,
      reason: extractProviderMessage(newest.errorMessage),
      targetIds: bucket.map((row) => row.id).sort(),
      postIds: Array.from(new Set(bucket.map((row) => row.postId))).sort(),
      count: bucket.length,
      lastFailedAt: newest.updatedAt,
      neverSucceeded: (publishedByPlatform[newest.platform] ?? 0) === 0,
      retryable: canRetry(bucket, kind),
    });
  }

  // Worst first: a channel that has never worked outranks one that broke once,
  // then the most recent, then the largest.
  return groups.sort((a, b) => {
    if (a.neverSucceeded !== b.neverSucceeded) return a.neverSucceeded ? -1 : 1;
    if (a.lastFailedAt !== b.lastFailedAt) return a.lastFailedAt > b.lastFailedAt ? -1 : 1;
    return b.count - a.count;
  });
}

/**
 * Dismissal identity, mirroring `attentionSignature`: the sorted set of failed
 * TARGET ids. Identity, never counts — so "one failure resolves, a different
 * one appears" cannot round-trip back to a dismissed signature and hide a new
 * problem forever.
 */
export function publishFailureSignature(groups: PublishFailureGroup[]): string {
  return groups
    .flatMap((group) => group.targetIds)
    .sort()
    .join(',');
}

export function shouldShowFailureStrip(
  groups: PublishFailureGroup[],
  dismissedSignature: string | null,
): boolean {
  if (groups.length === 0) return false;
  return publishFailureSignature(groups) !== dismissedSignature;
}

/**
 * The strip's headline. Names the account and quotes the platform, because
 * "something went wrong publishing" is exactly the non-answer this whole lane
 * exists to delete.
 */
export function describeFailureGroup(group: PublishFailureGroup): string {
  const platform = platformLabel(group.platform);
  const account = group.connectionName ? ` to ${group.connectionName}` : '';
  const attempts = group.count === 1 ? '1 post' : `${group.count} posts`;

  if (group.neverSucceeded) {
    return `${platform} has never published — ${attempts}${account} rejected`;
  }
  return `${platform} rejected ${attempts}${account}`;
}

/**
 * The sentence Home prints for a failing group, in plain words and with none of the provider's own
 * error text: what did not happen, then the one step that fixes it. The raw reason stays in the
 * delivery view and in the prompt handed to the agent.
 */
export function plainFailureSentence(group: PublishFailureGroup): string {
  const platform = platformLabel(group.platform);
  const posts = group.count === 1 ? 'a post' : `${group.count} posts`;
  const head = `${platform} has not accepted ${posts} from Mora.`;
  switch (group.kind) {
    case 'credential':
      return `${head} Reconnect ${platform} in Settings.`;
    case 'destination':
      return `${head} Choose the accounts to post to in Publishing.`;
    case 'media':
      return `${head} Review the posts in Publishing.`;
    default:
      return `${head} Open Publishing to see what happened.`;
  }
}

export interface PostFailureExplanation {
  kind: FailureKind;
  sentence: string;
  detail: string | null;
  action: 'reconnect' | 'retry';
}

// Codes whose remedy is to reconnect: the existing reconnect sentence and action stay in charge.
const CREDENTIAL_SHAPED_CODES = new Set([
  'token_expired',
  'fb_permission',
  'li_access_denied',
  'li_invalid_urn',
  'tt_scope',
  'tt_unaudited_private',
]);

export function explainPostFailure(
  raw: string | null | undefined,
  platforms: readonly string[] = [],
): PostFailureExplanation {
  const text = raw?.trim() ? raw : null;
  const kind = classifyFailure(text);
  const detail = extractProviderMessage(text);
  const named = platforms.filter((platform) => !isUnsupportedPublishPlatform(platform));
  const where = named.length === 1 ? platformLabel(named[0]) : 'the account';
  // A failure the platform spelled out (a rejected ratio, a text limit, a TikTok fail_reason) gets the
  // shared sentence for that platform instead of the generic one. Only when one platform is named, so the
  // rule set is the right one, and only for kinds with no richer remedy of their own.
  const specific =
    text && named.length === 1 && kind !== 'timeout' && kind !== 'destination'
      ? publishFailureMessage(named[0], text)
      : null;
  if (
    specific &&
    specific.code !== 'unknown' &&
    !specific.code.startsWith('http_') &&
    !CREDENTIAL_SHAPED_CODES.has(specific.code)
  ) {
    return { kind, sentence: specific.message, detail, action: 'retry' };
  }
  switch (kind) {
    case 'credential':
      return {
        kind,
        sentence: `Mora no longer has permission to post to ${where}. Reconnect it, then try again.`,
        detail,
        action: 'reconnect',
      };
    case 'media':
      return {
        kind,
        sentence: `The media could not be prepared for ${where}. Replace or recrop it, then try again.`,
        detail,
        action: 'retry',
      };
    case 'destination':
      return {
        kind,
        sentence: 'No account is chosen to post to. Choose one in the post, then try again.',
        detail,
        action: 'retry',
      };
    case 'timeout':
      return {
        kind,
        sentence: `This post never finished publishing, so Mora stopped waiting. It may or may not have reached ${where === 'the account' ? 'each channel' : where}; check before you try again.`,
        detail,
        action: 'retry',
      };
    default:
      return {
        kind,
        sentence: text
          ? `${where === 'the account' ? 'The platform' : where} did not accept this post. Review it, then try again.`
          : 'This post did not publish and no reason was recorded. Review it, then try again.',
        detail,
        action: 'retry',
      };
  }
}

export function failureNamedPlatforms(
  failingPlatform: string,
  postPlatforms: readonly string[] = [],
  deliveredPlatforms: readonly string[] = [],
): string[] {
  if (!isUnsupportedPublishPlatform(failingPlatform)) return [failingPlatform];
  return postPlatforms.filter(
    (platform) => !isUnsupportedPublishPlatform(platform) && !deliveredPlatforms.includes(platform),
  );
}

export function unpublishedPlatforms(post: {
  platforms?: unknown;
  platform_response?: unknown;
  platform_schedules?: unknown;
}): string[] | null {
  const evidence = Array.isArray(post.platform_response) ? post.platform_response : [];
  if (evidence.length === 0 || !Array.isArray(post.platforms)) return null;
  const intended = (post.platforms as unknown[]).filter(
    (value): value is string => typeof value === 'string' && !isUnsupportedPublishPlatform(value),
  );
  return intended.filter(
    (platform) =>
      platformOutcome(
        platform,
        post.platform_response,
        post.platform_schedules,
        isValidPlatformPostId,
      ).outcome !== 'published',
  );
}

export function deliveryDestinationPlatforms(
  targetPlatforms: readonly string[],
  postPlatforms: readonly string[] = [],
): string[] {
  const publishable = postPlatforms.filter((platform) => !isUnsupportedPublishPlatform(platform));
  const onlyUnsupported =
    targetPlatforms.length > 0 &&
    targetPlatforms.every((platform) => isUnsupportedPublishPlatform(platform));
  return onlyUnsupported && publishable.length > 0 ? publishable : [...targetPlatforms];
}

const RAW_FAILURE_PATTERN = /[{}]|^platform error:|request failed with status \d{3}|^error:/i;

export function isRawProviderFailure(raw: string | null | undefined): boolean {
  return Boolean(raw && RAW_FAILURE_PATTERN.test(raw.trim()));
}

const NEVER_SENT_PATTERN = /\bnever sent to ([a-z0-9_-]+(?:\s*,\s*[a-z0-9_-]+)*)/gi;

const LOWERCASE_PLATFORM_ID_PATTERN =
  /\b(facebook|instagram|linkedin|tiktok|twitter|pinterest|youtube|shopify|slack)\b|\b((?:for|to|on|from|with) )(threads)\b/g;

export function relabelPlatformsInMessage(text: string): string {
  return text
    .replace(NEVER_SENT_PATTERN, (_match, list: string) => {
      const labels = list.split(',').map((item) => platformLabel(item.trim()));
      return `never sent to ${labels.join(', ')}`;
    })
    .replace(
      LOWERCASE_PLATFORM_ID_PATTERN,
      (_match, id: string | undefined, lead: string | undefined, threads: string | undefined) =>
        id ? platformLabel(id) : `${lead ?? ''}${platformLabel(threads ?? '')}`,
    );
}

export function failureLine(
  raw: string | null | undefined,
  platforms: readonly string[] = [],
): { text: string; detail: string | null } {
  const trimmed = raw?.trim();
  if (!trimmed) return { text: 'Check the connection and try again.', detail: null };
  if (!isRawProviderFailure(trimmed)) {
    return { text: relabelPlatformsInMessage(trimmed), detail: null };
  }
  const explanation = explainPostFailure(trimmed, platforms);
  return { text: explanation.sentence, detail: explanation.detail };
}

export function deliveryScopeNote(input: {
  postsNeedingAttention: number;
  deliveryAttentionPosts: number;
  orphanedPosts: number;
  failedPosts?: number;
  partialPosts?: number;
}): string | null {
  const unexplained =
    input.postsNeedingAttention - input.deliveryAttentionPosts - input.orphanedPosts;
  if (unexplained <= 0) return null;
  const total = input.postsNeedingAttention;
  const headline = total === 1 ? '1 post needs attention.' : `${total} posts need attention.`;
  if (input.failedPosts !== undefined && input.partialPosts !== undefined) {
    const kinds: string[] = [];
    if (input.failedPosts > 0) kinds.push(`${input.failedPosts} didn't publish`);
    if (input.partialPosts > 0) {
      kinds.push(
        `${input.partialPosts} ${input.partialPosts === 1 ? 'was' : 'were'} only partly sent`,
      );
    }
    const split = kinds.length > 0 ? ` ${kinds.join(' and ')}.` : '';
    const here = input.deliveryAttentionPosts;
    const before = input.orphanedPosts;
    const listed: string[] = [];
    if (here > 0) listed.push(`${here} ${here === 1 ? 'is' : 'are'} listed here`);
    if (before > 0) listed.push(`${before} failed before reaching any account`);
    const listedSentence = listed.length > 0 ? ` ${listed.join(' and ')}.` : '';
    const other = `${listed.length > 0 ? 'The other ' : ''}${unexplained} ${unexplained === 1 ? 'is' : 'are'} listed in Posts, not here.`;
    return `${headline.slice(0, -1)}:${split}${listedSentence} ${other}`;
  }
  const rest =
    unexplained === 1
      ? '1 of them was only partly sent or never sent to some platforms, so it is listed in Posts, not here.'
      : `${unexplained} of them were only partly sent or never sent to some platforms, so they are listed in Posts, not here.`;
  return `${headline} ${rest}`;
}

/** The one action most likely to actually fix this group. */
export interface FailureRemedy {
  label: string;
  href: string;
}

export function remedyForGroup(group: PublishFailureGroup): FailureRemedy | null {
  switch (group.kind) {
    case 'credential':
      return { label: 'Reconnect', href: '/settings?connect=1' };
    case 'destination':
      return { label: 'Choose accounts', href: '/publishing/posts?status=failed' };
    case 'media':
      // Delivery, NOT a `?post=<id>` deep link. The post inspector opens
      // imperatively via `useInspector().openSubject` and has no URL param, so a
      // `?post=` link would navigate and then do nothing — a silent no-op
      // dressed as a fix. Delivery lists the exact failing posts and opens the
      // inspector properly from each row.
      return { label: 'Review', href: '/publishing/delivery' };
    default:
      return null;
  }
}

/**
 * The prompt handed to the agent. Carries the platform's verbatim message so
 * the agent reasons about the real error rather than a UI paraphrase of it.
 */
export function agentPromptForGroup(group: PublishFailureGroup): string {
  const account = group.connectionName ?? group.platform;
  const reason = group.reason ?? 'no error message was recorded';
  const scope = group.neverSucceeded
    ? `This connection has never successfully published.`
    : `This connection has published successfully before.`;
  return (
    `${group.count} post${group.count === 1 ? '' : 's'} failed to publish to my ` +
    `${platformLabel(group.platform)} account "${account}". ` +
    `${scope} The platform returned: "${reason}". ` +
    `What is actually causing this and what do I need to do to fix it?`
  );
}

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  twitter: 'X',
  pinterest: 'Pinterest',
  youtube: 'YouTube',
  threads: 'Threads',
  shopify: 'Shopify',
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform.toLowerCase()] ?? getPlatformLabel(platform);
}

/**
 * Posts that failed without ever producing a target row — they died before
 * fan-out, so no per-connection grain exists for them.
 *
 * `backfillMarked` is reported separately and never folded into an actionable
 * count. Hiding it entirely would be its own dishonesty (the rows exist, and a
 * user who counts them elsewhere deserves to know why this surface disagrees),
 * so the ledger states the exclusion in words instead of silently applying it.
 */
export interface OrphanedFailureSummary {
  actionable: number;
  backfillMarked: number;
}
